import React from 'react';
import {defineMessages} from 'react-intl';
import {
    AccessControlIndicator,
    defineAccessControlledResource,
    RestrictedGroupAccessRule,
    RestrictedPoliciesAccessRule,
} from '@igp/shared/access-control';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {LastUpdated} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';
import {momentToTimestampSeconds} from '@igp/shared/utils';

import {groups, useBoAgentAccessCheck, withAccessCheck} from '@access-control';
import {agentPolicy} from '@policy-access';
import type {AgentLevelReportV2FilterName} from '@features/agent-level-report-v2';
import {AgentLevelReportV2QueryFilters, firstAvailableDate} from '@features/agent-level-report-v2';
import type {NegativeCarryoverReportViewModelKeys} from '@features/negative-carryover-report';
import {
    DataGridNegativeCarryoverReport,
    NegativeCarryoverReportDownloadCsvButton,
    NegativeCarryoverWeeklySummaryDownloadButton,
    useNegativeCarryoverReport,
} from '@features/negative-carryover-report';

const localized = defineMessages({
    title: {
        id: 'NegativeCarryoverReport_title',
        defaultMessage: 'Negative Carryover Report',
    },
    weeklySummaryFilename: {
        id: 'NegativeCarryoverReport_weeklySummary',
        defaultMessage: 'CRM Credit Summary',
    },
    downloadWeeklySummary: {
        id: 'NegativeCarryoverReport_downloadWeeklySummary',
        defaultMessage: 'Export CRM Credit Summary',
    },
});

function NegativeCarryoverReportInner() {
    const viewType: ViewType = 'NegativeCarryoverReport';
    const keys: NegativeCarryoverReportViewModelKeys[] = [
        'agentUid',
        'weekStartDateTs',
        'totalNetShare',
        'debtBeforePayment',
        'weeklyActuallyPaid',
        'debtTransaction',
        'debtAfterPayment',
    ];
    const {
        items,
        rawItems,
        lastUpdate,
        totalCount,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        searchFilter,
        filterString,
    } = useNegativeCarryoverReport({
        fields: [...keys, 'id'],
        viewType,
        defaultPaging: {page: 1, pageSize: 25},
        hiddenFilters: [{key: 'week_start_date_ts.min', value: momentToTimestampSeconds(firstAvailableDate)}],
        validateFilter: () => true,
        syncWithUrl: true,
    });
    const isAgentFilterAvailable = useBoAgentAccessCheck(negativeCarryoverReportAgentFilterResource);
    const filters: AgentLevelReportV2FilterName[] = [isAgentFilterAvailable ? 'agentUid' : null, 'weekStartDate'].filter(
        i => i
    ) as AgentLevelReportV2FilterName[];
    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <>
                            <NegativeCarryoverWeeklySummaryDownloadButton
                                keys={[
                                    'agent_uid',
                                    'direction',
                                    'weekly_actually_paid',
                                    'reason_codes',
                                    'reference',
                                    'comments',
                                    'turnover_rate',
                                    'aal',
                                ]}
                                filter={searchFilter?.filter}
                                filterString={filterString}
                                filename={localized.weeklySummaryFilename}
                                label={localized.downloadWeeklySummary}
                                mode="server"
                            />
                            <NegativeCarryoverReportDownloadCsvButton
                                keys={keys}
                                filter={filterString}
                                filename={localized.title}
                                total={totalCount}
                                disabled={!totalCount}
                                mode="server"
                            />
                        </>
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems primary streched>
                            <AgentLevelReportV2QueryFilters model={filterString} onChange={handleFilterChange} availableFilters={filters} />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <>
                    <LastUpdated value={lastUpdate} />
                    <DataGridNegativeCarryoverReport
                        rows={items}
                        rawModels={rawItems}
                        paging={{
                            ...searchFilter.paging,
                            rowCount: totalCount,
                        }}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSortChange={handleSortChange}
                        sortModel={{
                            sortModel: searchFilter?.sorting,
                        }}
                        columns={keys}
                    />
                </>
            }
        />
    );
}

export const negativeCarryoverReportAgentFilterResource = defineAccessControlledResource({
    name: 'NegativeCarryoverReportAgentFilterResource',
    rules: new RestrictedPoliciesAccessRule([agentPolicy]),
});

export const negativeCarryOverReportPageResource = defineAccessControlledResource({
    name: 'NegativeCarryOverReportPageResource',
    rules: new RestrictedGroupAccessRule([groups.aalRowGroup, groups.aalRowManagersGroup]),
});

const NegativeCarryoverReportWithAccess = withAccessCheck(
    NegativeCarryoverReportInner,
    negativeCarryOverReportPageResource,
    AccessControlIndicator.InfoMessage
);

export const NegativeCarryoverReport = withTraceErrorBoundary(NegativeCarryoverReportWithAccess, ErrorModal);
